import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Stack,
  Title,
  Text,
  TextInput,
  Button,
  Alert,
  Image,
  Group,
} from "@mantine/core";
import { getApiCompany, getGlobalApiEndpoint } from "../../config/config";
import rNameLogo from "../../assets/Reppi.svg";

function ForgotPasswordPage() {
  const [company, setCompany] = useState(getApiCompany());
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Pre-auth like login: runs against the global host with the company
      // in the payload rather than the URL.
      const trimmedCompany = company.trim();
      const response = await fetch(
        getGlobalApiEndpoint("/v1/user/forgot-password"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, company: trimmedCompany }),
        }
      );

      await response.json();

      if (response.ok) {
        setSuccess(
          "We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password."
        );
        setEmail(""); // Clear the email field
      } else {
        setError("Failed to send reset email. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-secondary)",
        padding: "20px",
      }}
    >
      <Container size={500}>
        <Box
          bg="white"
          style={{
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "60px 40px",
            textAlign: "center",
          }}
        >
          <Box
            mb={40}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
          >
            <Image src={rNameLogo} alt="Reppi" h={60} w="auto" mx="auto" />
          </Box>

          <Stack gap="md" mb={32}>
            <Title order={2} size="2xl" fw={600} c="var(--color-text-primary)">
              Forgot Password?
            </Title>
            <Text size="sm" c="var(--color-text-secondary)" lh={1.5}>
              Enter your email address and we'll send you a link to reset your
              password.
            </Text>
          </Stack>

          {/* Success Alert */}
          {success && (
            <Alert title="Success!" color="green" mb="lg" ta="left">
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert title="Error" color="red" mb="lg" ta="left">
              {error}
            </Alert>
          )}

          {success ? (
            <Group justify="center" mb={32}></Group>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label="Company"
                  placeholder="Enter your company"
                  value={company}
                  onChange={e => setCompany(e.currentTarget.value)}
                  required
                  size="md"
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                    },
                  }}
                />

                <TextInput
                  label="Email address"
                  placeholder="Enter your email address"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.currentTarget.value)}
                  required
                  size="md"
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                    },
                  }}
                />

                <Button type="submit" size="md" loading={loading} fullWidth>
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </Stack>
            </Box>
          )}

          <Group justify="center" mt={24}>
            <Button variant="subtle" size="sm" onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  );
}

export default ForgotPasswordPage;
